import React, { useState } from "react";
import {
  Box, Button, Flex, FormControl, FormErrorMessage, FormLabel,
  Grid, Input, Switch, Text, useColorModeValue,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { LeaderSearchInput } from "../../../../components/LeaderSearchInput";

const maskCPF = (raw: string): string => {
  const d = raw.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
};

const maskPhone = (raw: string): string => {
  const d = raw.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2)  return `(${d}`;
  if (d.length <= 7)  return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

export interface AddCandidateFormData {
  email: string;
  candidate_full_name: string | null;
  cpf_number: string | null;
  candidate_phone: string | null;
  responsible_manager: string | null;
  is_pj: boolean | null;
}

interface AddCandidateFormProps {
  isSubmitting: boolean;
  onSubmit: (data: AddCandidateFormData) => void;
}

export const AddCandidateForm = ({ isSubmitting, onSubmit }: AddCandidateFormProps) => {
  const [email, setEmail]         = useState("");
  const [name, setName]           = useState("");
  const [cpf, setCpf]             = useState("");
  const [phone, setPhone]         = useState("");
  const [manager, setManager]     = useState("");
  const [isPj, setIsPj]           = useState(false);
  const [emailError, setEmailError] = useState("");

  const cardBg      = useColorModeValue("white", "#0f1f3a");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.100");
  const inputBg     = useColorModeValue("gray.50", "#0a1628");
  const labelColor  = useColorModeValue("gray.500", "gray.400");
  const sectionBg   = useColorModeValue("gray.50", "#0a1628");

  const validateEmail = (v: string): string => {
    if (!v) return "E-mail obrigatório.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v))
      return "Formato de e-mail inválido (ex: nome@gmail.com).";
    return "";
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = email.trim().toLowerCase();
    const err = validateEmail(normalized);
    if (err) { setEmailError(err); return; }

    onSubmit({
      email: normalized,
      candidate_full_name: name.trim() || null,
      cpf_number: cpf.replace(/\D/g, "") || null,
      candidate_phone: phone.replace(/\D/g, "") || null,
      responsible_manager: manager.trim() || null,
      is_pj: isPj,
    });

    setEmail(""); setName(""); setCpf(""); setPhone(""); setManager(""); setIsPj(false); setEmailError("");
  };

  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="2xl"
      overflow="hidden"
      boxShadow="sm"
    >
      <Box px={5} pt={4} pb={3} bg={sectionBg} borderBottom="1px solid" borderColor={borderColor}>
        <Text fontSize="xs" fontWeight={700} color={labelColor} textTransform="uppercase" letterSpacing="0.08em">
          Autorizar novo candidato
        </Text>
      </Box>

      <Box px={5} py={4}>
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3} mb={3}>
          <FormControl isInvalid={!!emailError}>
            <FormLabel fontSize="xs" fontWeight={600} color={labelColor} textTransform="uppercase" letterSpacing="0.05em" mb={1.5}>
              E-mail <Box as="span" color="red.400">*</Box>
            </FormLabel>
            <Input
              placeholder="candidato@empresa.com"
              type="text"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(""); }}
              onBlur={() => { if (email.trim()) setEmailError(validateEmail(email.trim().toLowerCase())); }}
              isDisabled={isSubmitting}
              bg={inputBg} borderRadius="lg" size="sm" fontSize="sm"
              _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
            />
            <FormErrorMessage fontSize="xs" mt={1}>{emailError}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="xs" fontWeight={600} color={labelColor} textTransform="uppercase" letterSpacing="0.05em" mb={1.5}>
              Nome completo
              <Box as="span" ml={1.5} fontSize="10px" fontWeight={400} color={labelColor} textTransform="none" letterSpacing="normal">(opcional)</Box>
            </FormLabel>
            <Input
              placeholder="João da Silva" value={name}
              onChange={(e) => setName(e.target.value)}
              isDisabled={isSubmitting}
              bg={inputBg} borderRadius="lg" size="sm" fontSize="sm"
              _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
            />
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3} mb={3}>
          <FormControl>
            <FormLabel fontSize="xs" fontWeight={600} color={labelColor} textTransform="uppercase" letterSpacing="0.05em" mb={1.5}>
              CPF
              <Box as="span" ml={1.5} fontSize="10px" fontWeight={400} color={labelColor} textTransform="none" letterSpacing="normal">(opcional)</Box>
            </FormLabel>
            <Input
              placeholder="000.000.000-00" value={cpf}
              onChange={(e) => setCpf(maskCPF(e.target.value))}
              maxLength={14} isDisabled={isSubmitting}
              bg={inputBg} borderRadius="lg" size="sm" fontSize="sm"
              _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize="xs" fontWeight={600} color={labelColor} textTransform="uppercase" letterSpacing="0.05em" mb={1.5}>
              Telefone
              <Box as="span" ml={1.5} fontSize="10px" fontWeight={400} color={labelColor} textTransform="none" letterSpacing="normal">(opcional)</Box>
            </FormLabel>
            <Input
              placeholder="(00) 00000-0000" value={phone}
              onChange={(e) => setPhone(maskPhone(e.target.value))}
              maxLength={16} isDisabled={isSubmitting}
              bg={inputBg} borderRadius="lg" size="sm" fontSize="sm"
              _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
            />
          </FormControl>
        </Grid>

        <Grid templateColumns="1fr" gap={3} mb={4}>
          <FormControl>
            <FormLabel fontSize="xs" fontWeight={600} color={labelColor} textTransform="uppercase" letterSpacing="0.05em" mb={1.5}>
              Gestor responsável
              <Box as="span" ml={1.5} fontSize="10px" fontWeight={400} color={labelColor} textTransform="none" letterSpacing="normal">(opcional)</Box>
            </FormLabel>
            <LeaderSearchInput
              value={manager} onChange={setManager}
              placeholder="Buscar gestor..." size="sm" bg={inputBg} isDisabled={isSubmitting}
            />
          </FormControl>
        </Grid>

        <Flex align="center" justify="space-between" pt={3} borderTop="1px solid" borderColor={borderColor} gap={3} flexWrap="wrap">
          <FormControl display="flex" alignItems="center" gap={3} w="auto" flexShrink={0}>
            <Switch id="is-pj-input" colorScheme="orange" isChecked={isPj}
              onChange={(e) => setIsPj(e.target.checked)} isDisabled={isSubmitting} size="sm" />
            <Box>
              <FormLabel htmlFor="is-pj-input" mb={0} fontSize="sm" fontWeight={500} cursor="pointer" lineHeight={1.2}>
                Contratação PJ
              </FormLabel>
              <Text fontSize="10px" color={labelColor} lineHeight={1.3}>Candidato será contratado como PJ</Text>
            </Box>
          </FormControl>

          <Button leftIcon={<AddIcon boxSize="10px" />} colorScheme="blue" type="submit"
            isLoading={isSubmitting} size="sm" borderRadius="lg" px={5} fontWeight={600}>
            Autorizar candidato
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};
